import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import rooms from 'domains/wall_preview_rooms';
import { calculateWallPreviewImageSize } from 'utils/math_util';

import type { Dimension, ImageNaturalDimension } from 'types/common_types';

export type ScreenDimension = Dimension & {
  smallest: number;
};

export type WallPreviewDimension = ScreenDimension & ImageNaturalDimension;

type StageSliceInitialState = {
  screen: ScreenDimension;
  wallPreview: WallPreviewDimension | null;
  zoom: number;
  toastMessage?: string;
};

const initialState: StageSliceInitialState = {
  screen: {
    width: 0,
    height: 0,
    smallest: 0,
  },
  wallPreview: null,
  zoom: 1,
};

const stage = createSlice({
  name: 'stage',
  initialState,
  reducers: {
    setScreenDimensions(state, { payload }) {
      let { width, height } = payload;

      // There are cases where we need to divide by the screen or node dimensions
      // So don't ever let these be 0
      width = width > 0 ? width : 1;
      height = height > 0 ? height : 1;

      const update = {
        ...state,
        screen: {
          width,
          height,
          smallest: Math.min(width, height),
        },
      };

      // Update the wall preview size if it's on
      if (update.wallPreview) {
        const nextSize = calculateWallPreviewImageSize(update.screen, {
          width: update.wallPreview.naturalWidth,
          height: update.wallPreview.naturalHeight,
        });

        update.wallPreview = {
          ...update.wallPreview,
          width: nextSize.width,
          height: nextSize.height,
          smallest: Math.min(nextSize.height, nextSize.width),
        };
      }

      return update;
    },
    setStageZoom(state, { payload }: PayloadAction<number>) {
      return {
        ...state,
        zoom: payload,
      };
    },

    setToastMessage(state, { payload }: PayloadAction<string>) {
      return {
        ...state,
        toastMessage: payload,
      };
    },
  },
  extraReducers(builder) {
    builder
      .addCase(rooms.actions.wallPreviewImageLoad.fulfilled, (state, { payload }) => {
        const { naturalWidth, naturalHeight } = payload;
        const size = calculateWallPreviewImageSize(state.screen, {
          width: naturalWidth,
          height: naturalHeight,
        });

        return {
          ...state,
          zoom: 1,
          wallPreview: {
            ...payload,
            ...size,
            smallest: Math.min(size.width, size.height),
          },
        };
      })
      .addCase(rooms.actions.unsetWhichWallPreview, (state) => {
        return {
          ...state,
          wallPreview: null,
        };
      })
      .addCase('canvases/resetToSingleCanvas', (state) => {
        return {
          ...state,
          zoom: 1,
        };
      });
  },
});

export default stage;
